import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStatus, useLogout } from '@/hooks/useApi';
import {
  User,
  LogOut,
  HelpCircle,
  Shield,
  ChevronUp,
  Settings,
  Database,
  Scale,
  Bell
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/utils/cn';
import { NAV_HOVER, NAV_MUTED } from './navTheme';

/** The signed-in user, at the foot of the sidebar: profile, settings, help and sign-out. Collapsed, just the
 *  initials (the name is the button's label). */
const UserMenu: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const { data: authStatus } = useAuthStatus();
  const logoutMutation = useLogout();

  if (!authStatus?.user) {
    return null;
  }

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const userInitials = authStatus.user.full_name
    ? authStatus.user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()
    : authStatus.user.username.substring(0, 2).toUpperCase();

  const isAdmin = authStatus.user?.roles?.includes('admin');
  const isSysAdmin = authStatus.user?.roles?.includes('sysadmin');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={collapsed ? `Account: ${authStatus.user.full_name || authStatus.user.username}` : undefined}
          title={collapsed ? authStatus.user.full_name || authStatus.user.username : undefined}
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors",
            NAV_HOVER, "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-mp-accent",
            collapsed && "justify-center"
          )}>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-mp-accent">
            <span className="text-mp-sm font-semibold text-mp-accent-contrast">{userInitials}</span>
          </div>
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="truncate text-mp-nav-size font-medium leading-4">
                  {authStatus.user.full_name || authStatus.user.username}
                </div>
                <div className={cn('truncate text-xs leading-4', NAV_MUTED)}>
                  {authStatus.user.roles.join(' · ')}
                </div>
              </div>
              <ChevronUp className={cn('h-4 w-4 shrink-0', NAV_MUTED)} />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={collapsed ? 'right' : 'top'} align={collapsed ? 'end' : 'start'} className="w-64">
        {/* User Info Section */}
        <div className="px-3 py-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-gray-700">{userInitials}</span>
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {authStatus.user.full_name || authStatus.user.username}
              </div>
              <div className="text-sm text-gray-500">
                {authStatus.user.email}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {authStatus.user?.roles?.map((role: string, index: number) => (
                  <span key={role} className="inline-flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    {role}
                    {index < (authStatus.user?.roles?.length || 0) - 1 && ' • '}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Menu Actions */}
        <DropdownMenuItem
          onClick={() => navigate('/profile')}
          className="cursor-pointer"
        >
          <User className="h-4 w-4 mr-2" />
          <span>My Profile</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => navigate('/settings/notifications')}
          className="cursor-pointer"
        >
          <Bell className="h-4 w-4 mr-2" />
          <span>Notification Settings</span>
        </DropdownMenuItem>

        {/* System Admin Section - only for sysadmin role */}
        {isSysAdmin && (
          <>
            <DropdownMenuSeparator />

            <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              System
            </div>

            <DropdownMenuItem
              onClick={() => navigate('/admin/import-queue')}
              className="cursor-pointer"
            >
              <Database className="h-4 w-4 mr-2" />
              <span>Import Queue</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => navigate('/admin/balance-variance')}
              className="cursor-pointer"
            >
              <Scale className="h-4 w-4 mr-2" />
              <span>Balance Variance Report</span>
            </DropdownMenuItem>
          </>
        )}

        {/* Admin Section - for business admin role */}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />

            <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Administration
            </div>

            <DropdownMenuItem
              onClick={() => navigate('/admin/users')}
              className="cursor-pointer"
            >
              <Settings className="h-4 w-4 mr-2" />
              <span>User Management</span>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => navigate('/help')}
          className="cursor-pointer"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          <span>Help & Documentation</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4 mr-2" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;